import React, { useState, useRef, useMemo} from 'react';
import { useNavigate } from "react-router-dom";
import { AgGridReact } from 'ag-grid-react';
import { FaEye, FaDownload, FaTrashAlt, FaCheckCircle, FaTimesCircle, FaQuestionCircle  } from 'react-icons/fa';
import useFileDelete from '../api/fileDelete';
import useFilePreview from '../api/filePreview';
import ActionBar from './ActionBar';
import PreviewItem from "./PreviewItem";
import useFileDownload from '../api/fileDownload';
import useFileAnalysis from '../api/fileAnalysis';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'; 
import { useWebSocketContext } from '../contexts/WebSocketProvider';

// AG Grid styles
import '../styles/DocumentList.css';

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

const DocumentList = ({setToasts, container_name}) => {
  const gridRef = useRef(null);
  const [previewedFile, setPreviewedFile] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const { documents } = useWebSocketContext();
  const navigate = useNavigate();
  const { analysisAllFiles } = useFileAnalysis(navigate, container_name);
  const { previewFile } = useFilePreview(navigate, container_name);
  const { deleteFile, deleteFiles } = useFileDelete(navigate, container_name);
  const { downloadFile, downloadFiles } = useFileDownload(navigate, container_name);



  const handlePreview = async (file) => {
    const data = await previewFile(file.name);
    setPreviewedFile({
      name: file.name,
      sasUrl: data.sas_url,
    });
    setIsPreviewOpen(true);
  };

  const handleDownload = async (file) => {
    try {
      await downloadFile(file.name);
    } catch (error) {
      console.log(`Error downloading file: ${file.name}`, error);
    }
  };

  const batchDownload = async() => {
    const selectedFiles = gridRef.current.api.getSelectedRows();
    const filenames = selectedFiles.map(file => file.name);
    const toastId = Date.now();
    // Add a new toast
    setToasts((prev) => [
      ...prev,
      { id: toastId, label: "Downloading Files", progress: 0 },
    ]);
    try {
      await downloadFiles(
        filenames, 
        (progress) => {
          // Update progress in the toast
          setToasts((prev) =>
            prev.map((toast) =>
              toast.id === toastId ? { ...toast, progress } : toast
            )
          );
        });
      setToasts((prev) =>
        prev.map((toast) =>
          toast.id === toastId ? { ...toast, progress: 100 } : toast
        )
      );
    } catch (error) {
      // Mark the toast with an error message
      setToasts((prev) =>
        prev.map((toast) =>
          toast.id === toastId
            ? { ...toast, label: "Error downloading files", progress: 0 }
            : toast
        )
      );
    }
    
  };

  const handleDelete = async (file) => {
    await deleteFile(file.name);
  };


  const batchDelete = async () => {
    const selectedFiles = gridRef.current.api.getSelectedRows();
    const filenames = selectedFiles.map(file => file.name);
    const toastId = Date.now();
    // Add a new toast
    setToasts((prev) => [
      ...prev,
      { id: toastId, label: `Delete Files`, progress: 0 },
    ]);
    try {
      await deleteFiles(
        filenames,
        (progress) => {
          // Update progress in the toast
          setToasts((prev) =>
            prev.map((toast) =>
              toast.id === toastId ? { ...toast, progress } : toast
            )
          );
        });

      setToasts((prev) =>
        prev.map((toast) =>
          toast.id === toastId ? { ...toast, progress: 100 } : toast
        )
      );
    } catch (error) {
      // Mark the toast with an error message
      setToasts((prev) =>
        prev.map((toast) =>
          toast.id === toastId
            ? { ...toast, label: "Error deleting files", progress: 0 }
            : toast
        )
      );
    }
  };

  // AG Grid column definitions
  const rowSelection = useMemo(() => {
    return {
      mode: "multiRow",
    };
  }, []);
  const columnDefs = [
    {
      headerName: "File Name",
      field: "name",
      flex: 1,
    },
    {
      headerName: "Status",
      field: "status",
      width: 150,
      cellRenderer: (params) => {
        if (params.data.status === "true") {
          return <FaCheckCircle className="icon success" title="Processed" />;
        } else if (params.data.status === "false") {
          return <FaTimesCircle className="icon error" title="Failed" />;
        } else if (params.data.status === null) {
          return <FaQuestionCircle className="icon pending" title="Pending" />;
        }
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 150,
      cellRenderer: (params) => (
        <div className="actions">
          <FaEye
            className="icon preview"
            onClick={() => handlePreview(params.data)}
            title="Preview"
          />
          <FaDownload
            className="icon download"
            onClick={() => handleDownload(params.data)}
            title="Download"
          />
          <FaTrashAlt
            className="icon delete"
            onClick={() => handleDelete(params.data)}
            title="Delete"
          />
        </div>
      ),
    },
  ];

  return (
    <div className='document-list-container'>
      <ActionBar
        batchDownload={batchDownload}
        batchDelete={batchDelete}
        fullAnalysis={analysisAllFiles}
      />
      <div style={{ height: 500, width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          rowData={documents}
          columnDefs={columnDefs}
          rowSelection={rowSelection}
          animateRows={true}
        />
      </div>
      <PreviewItem
        file={previewedFile}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)} // Clear the previewed file
      />
    </div>
  );
};

export default DocumentList;